import { createRoute, useSearch } from '@tanstack/react-router';
import { Button, VerydiaWordmark } from '@verydia/ui';
import { RotateCcw } from 'lucide-react';
import { rootRoute } from '../root.tsx';
import { useAuth } from '../../lib/auth/index.ts';

/**
 * What each failure means, in the operator's terms.
 *
 * The raw OAuth error code is shown too, because it is what makes a support
 * conversation short — but it is never the whole message, since "invalid_grant"
 * tells a user nothing about what to do next.
 */
const EXPLANATIONS: Readonly<Record<string, string>> = {
  state_mismatch:
    'The sign-in response did not match the request this browser made. This can happen if the ' +
    'sign-in page was left open for a long time, or if the link was opened from somewhere else. ' +
    'Starting again is safe.',
  invalid_callback:
    'The sign-in response was incomplete. This usually means the page was reloaded part-way ' +
    'through sign-in.',
  invalid_grant:
    'The sign-in code had already been used or had expired. Each one works exactly once.',
  token_exchange_failed: 'The identity provider did not complete the sign-in.',
  identity_provider_unreachable:
    'The sign-in service could not be reached. Check your connection and try again.',
  access_denied: 'The sign-in was cancelled, or this account is not permitted to sign in.',
};

function AuthErrorPage() {
  const auth = useAuth();
  const search = useSearch({ strict: false }) as { reason?: string };
  const reason = search.reason ?? 'token_exchange_failed';

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--vc-surface-sunken)] px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <VerydiaWordmark tone="surface" />
        </div>

        <div
          className="rounded-xl border border-[var(--vc-border)] bg-[var(--vc-surface)] p-8 shadow-sm"
          role="alert"
        >
          <h1 className="text-lg font-semibold text-[var(--vc-text)]">Sign-in did not complete</h1>
          <p className="mt-2 text-sm text-[var(--vc-text-subtle)]">
            {EXPLANATIONS[reason] ?? 'The sign-in could not be completed.'}
          </p>

          <Button
            variant="primary"
            icon={<RotateCcw />}
            className="mt-6 w-full justify-center"
            onClick={() => auth.signIn('/')}
          >
            Try signing in again
          </Button>

          <p className="mt-4 text-xs text-[var(--vc-text-subtle)]">
            Reference: <span className="font-mono">{reason}</span>
          </p>
        </div>
      </div>
    </main>
  );
}

export const authErrorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/error',
  component: AuthErrorPage,
  validateSearch: (search: Record<string, unknown>) => ({
    reason: typeof search['reason'] === 'string' ? search['reason'] : undefined,
  }),
});
